import { useState } from 'react';
import { DEFAULT_PAGE_SIZE, API_BASE_URL } from '@/utils/products/constants';

export const useProductList = (showNotification, navigate) => {
    // Products state
    const [products, setProducts] = useState([]);
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: DEFAULT_PAGE_SIZE,
        hasMore: true
    });
    
    // New product state
    const [newProduct, setNewProduct] = useState({
        name: "",
        seoSlug: ""
    });
    const [creatingProduct, setCreatingProduct] = useState(false);
    
    // Fetch products with pagination from the API
    const fetchProducts = async (offset = 0, limit = DEFAULT_PAGE_SIZE, append = false) => {
        // If appending (load more), use loadingMore state, otherwise use loading state
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/product/list?limit=${limit}&offset=${offset}`);
            const result = await response.json();
            
            if (result.isSuccess) {
                // If appending, combine the new products with existing ones
                if (append) {
                    setProducts(prevProducts => [...prevProducts, ...result.data]);
                } else {
                    setProducts(result.data);
                }
                
                // Update pagination state
                const hasMore = result.data.length === limit; // If we got back the requested limit, there might be more
                setPagination(prev => ({
                    ...prev,
                    offset: append ? offset + limit : limit,
                    hasMore
                }));
            } else {
                throw new Error(result.message || "Failed to fetch products");
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            showNotification("Failed to load product data", "error");
            setError(error.message);
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    };
    
    // Handle new product input changes
    const handleNewProductChange = (e) => {
        const { name, value } = e.target;
        setNewProduct(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Handle creating a new product
    const handleCreateProduct = async (productData) => {
        if (!productData.name.trim()) {
            showNotification("Product name is required", "error");
            return false;
        }
        
        setCreatingProduct(true);
        
        try {
            const response = await fetch(`${API_BASE_URL}/product/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: productData.name.trim(),
                    seoSlug: productData.seoSlug.trim()
                })
            });
            
            const result = await response.json();
            
            if (result.isSuccess) {
                showNotification("Product created successfully", "success");
                
                // Navigate to the product detail page
                navigate(`/cms/product/${result.data.id}`);
                return true;
            } else {
                throw new Error(result.message || "Failed to create product");
            }
        } catch (error) {
            console.error("Error creating product:", error);
            showNotification(error.message || "Failed to create product", "error");
            return false;
        } finally {
            setCreatingProduct(false);
        }
    };
    
    // Handle editing a product
    const handleEdit = (productId) => {
        navigate(`/cms/product/${productId}`);
    };
    
    // Handle deleting a product
    const handleDelete = async (productId) => {
        if (window.confirm("Are you sure you want to delete this product?")) {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/product/${productId}/delete`, {
                    method: 'POST',
                });
                
                const result = await response.json();
                
                if (result.isSuccess) {
                    // Refresh the current page
                    fetchProducts(0, pagination.limit);
                    showNotification("Product deleted successfully", "success");
                } else {
                    throw new Error(result.message || "Failed to delete product");
                }
            } catch (error) {
                console.error("Error deleting product:", error);
                showNotification(error.message || "Failed to delete product", "error");
            } finally {
                setLoading(false);
            }
        }
    };
    
    return {
        products,
        loading,
        loadingMore,
        error,
        pagination,
        setPagination,
        fetchProducts,
        newProduct,
        setNewProduct,
        creatingProduct,
        handleEdit,
        handleDelete,
        handleCreateProduct,
        handleNewProductChange
    };
};